import { useEffect, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import {
  ArrowDownUp,
  CalendarDays,
  Clock,
  FileVideo,
  Filter,
  Loader2,
  Monitor,
  Play,
  PlayCircle,
  Trash2,
} from 'lucide-react';
import { Badge, buttonClasses, Card, cn, EmptyState, ErrorState, LoadingState } from '@pixlide/ui';
import { ApiError } from '@/api/client';
import { frameClassForFormat } from '@/lib/video-format';
import { ProtectedVideoPreview } from '@/features/videos/ProtectedVideoPreview';
import { useProjects } from '@/api/queries/dashboard';
import { useArchiveProject } from '@/api/queries/projects';
import type { components } from '@/api/generated/schema';

type ProjectListItem = components['schemas']['PixlideApplicationFeaturesVideoProjectsProjectListItem'];

/**
 * Filtre d'origine, tel qu'exposé au client.
 *
 * `undefined` = Tous. Les valeurs numériques suivent `VideoProjectOrigin` : 0 = à partir
 * d'un template, 1 = commande sur-mesure. On ignore volontairement les valeurs au-delà
 * (aucune autre origine n'est encore produite côté API).
 */
const ORIGIN_FILTERS = [
  { label: 'Tous', origin: undefined },
  { label: 'Templates', origin: 0 },
  { label: 'Vidéo sur-mesure', origin: 1 },
] as const;

type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger';

/**
 * Badge d'une carte. Une vidéo prête se distingue selon qu'elle passe à l'antenne : c'est la
 * seule différence qui compte pour le client entre deux vidéos terminées.
 */
function statusBadge(project: ProjectListItem): { label: string; tone: BadgeTone } {
  switch (project.status) {
    case 0:
      return { label: 'Brouillon', tone: 'warning' };
    case 1:
      return { label: 'En cours', tone: 'brand' };
    case 3:
      return { label: 'Échec', tone: 'danger' };
    default:
      return project.broadcastScreenCount > 0
        ? { label: 'En diffusion', tone: 'success' }
        : { label: 'Inactif', tone: 'neutral' };
  }
}

/** Date courte des cartes (« 28.03.25 ») : la place manque pour le mois en toutes lettres. */
function formatShortDate(value: string): string {
  return new Date(value)
    .toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })
    .replaceAll('/', '.');
}

function formatDuration(durationMs: number | null | undefined): string {
  return durationMs ? `${Math.round(durationMs / 1000)} s` : '—';
}

/**
 * Horloge locale qui n'avance que pendant un rendu.
 *
 * Le compte à rebours ne peut pas dépendre du seul sondage réseau : il ne bougerait que
 * toutes les 5 secondes, par à-coups, et donnerait l'impression d'un écran figé — ce qu'on
 * cherche justement à corriger. L'intervalle est démonté dès qu'aucun rendu n'est en cours.
 */
function useSecondsTicker(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) {
      return;
    }

    const timer = setInterval(() => setNow(Date.now()), 1_000);

    return () => clearInterval(timer);
  }, [active]);

  return now;
}

/**
 * Attente annoncée sous la roue d'attente.
 *
 * Sans estimation (premier rendu d'un template, historique vide), on annonce l'activité sans
 * inventer de délai : une promesse fausse est pire qu'une absence de promesse. Passé
 * l'estimation, on bascule sur « Finalisation » plutôt que d'afficher un compte à rebours
 * négatif ou bloqué à zéro.
 */
function renderWaitLabel(
  startedAt: string | null | undefined,
  estimatedSeconds: number | null | undefined,
  now: number,
): string {
  if (!startedAt || !estimatedSeconds) {
    return 'Rendu en cours';
  }

  const elapsedSeconds = (now - new Date(startedAt).getTime()) / 1000;
  const remainingSeconds = Math.ceil(estimatedSeconds - elapsedSeconds);

  return remainingSeconds > 0 ? `Disponible dans ~${remainingSeconds} s` : 'Finalisation…';
}

function CardStat({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex flex-1 flex-col items-center gap-1 text-ink-300" title={label}>
      <span aria-hidden>{icon}</span>
      <span className="sr-only">{label} :</span>
      <span className="text-[11px] text-ink-900">{value}</span>
    </div>
  );
}

/**
 * Brouillon : rien à lire, donc les deux seules actions utiles à la place du lecteur. La
 * suppression demande une confirmation, faute de corbeille côté client.
 */
function DraftActions({ project }: { project: ProjectListItem }) {
  const archive = useArchiveProject();
  const [isConfirming, setIsConfirming] = useState(false);
  const archiveError = archive.error instanceof ApiError ? archive.error.message : null;

  return (
    <div
      className={cn(
        'flex flex-col items-stretch justify-center gap-2.5 rounded-input bg-warning-100/60 px-4',
        frameClassForFormat(project.format),
      )}
    >
      <Link
        to="/creer/template/$projectId"
        params={{ projectId: project.id }}
        className="flex items-center justify-center gap-2 rounded-input bg-ink-900 px-3 py-2 text-xs font-medium text-white shadow-card transition hover:bg-ink-700"
      >
        <Play aria-hidden className="size-3" fill="currentColor" />
        Reprendre le projet
      </Link>

      <button
        type="button"
        disabled={archive.isPending}
        onClick={() => (isConfirming ? archive.mutate(project.id) : setIsConfirming(true))}
        className="flex items-center justify-center gap-2 rounded-input bg-white px-3 py-2 text-xs font-medium text-ink-900 shadow-card ring-1 ring-ink-100 transition hover:ring-danger-500 disabled:opacity-50"
      >
        <Trash2 aria-hidden className="size-3.5 text-danger-500" />
        {isConfirming ? 'Confirmer la suppression' : 'Supprimer le projet'}
      </button>

      {archiveError ? (
        <p role="alert" className="text-center text-[11px] text-danger-500">
          {archiveError}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Mes vidéos : bibliothèque des projets, filtrable par origine.
 *
 * Un brouillon (status Draft) propose la reprise de création ou la suppression plutôt
 * qu'un lecteur : il n'a encore rien à montrer.
 */
export function VideosPage() {
  const [origin, setOrigin] = useState<0 | 1 | undefined>(undefined);
  const [showFilters, setShowFilters] = useState(true);
  const [oldestFirst, setOldestFirst] = useState(false);
  const projects = useProjects(origin === undefined ? { pageSize: 24 } : { origin, pageSize: 24 });
  const now = useSecondsTicker((projects.data?.items ?? []).some((project) => project.status === 1));

  // L'API trie du plus récent au plus ancien ; l'ordre inverse s'applique à la page chargée.
  const items = oldestFirst ? [...(projects.data?.items ?? [])].reverse() : (projects.data?.items ?? []);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <h1 className="text-4xl font-medium tracking-tight">Mes vidéos</h1>

      <div className="flex items-center gap-3 rounded-2xl bg-ink-100/60 px-4 py-2.5 text-sm font-medium">
        <button
          type="button"
          aria-expanded={showFilters}
          onClick={() => setShowFilters((current) => !current)}
          className="flex items-center gap-1.5 text-ink-900 transition hover:text-ink-500"
        >
          <Filter aria-hidden className="size-4" />
          Filtres
        </button>

        <span aria-hidden className="h-4 w-px bg-ink-300" />

        <button
          type="button"
          aria-pressed={oldestFirst}
          onClick={() => setOldestFirst((current) => !current)}
          className="flex items-center gap-1.5 text-ink-900 transition hover:text-ink-500"
        >
          <ArrowDownUp aria-hidden className="size-4" />
          Trier par dates
          <span className="text-xs font-normal text-ink-500">
            ({oldestFirst ? 'plus anciennes' : 'plus récentes'})
          </span>
        </button>
      </div>

      {showFilters ? (
        <div className="flex flex-wrap gap-2">
          {ORIGIN_FILTERS.map((filter) => (
            <button
              key={filter.label}
              type="button"
              aria-pressed={origin === filter.origin}
              onClick={() => setOrigin(filter.origin)}
              className={cn(
                'rounded-pill px-4 py-1.5 text-sm transition',
                origin === filter.origin
                  ? 'bg-white text-ink-900 shadow-card'
                  : 'text-ink-700 ring-1 ring-ink-300 hover:bg-white/60',
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
      ) : null}

      {projects.isPending ? <LoadingState /> : null}

      {projects.isError ? (
        <ErrorState
          message="Impossible de charger vos vidéos pour le moment."
          onRetry={() => void projects.refetch()}
        />
      ) : null}

      {projects.data?.items.length === 0 ? (
        <EmptyState
          icon={<FileVideo className="size-10" strokeWidth={1.25} />}
          title="Aucun projet pour le moment"
          description="Créez votre première vidéo à partir d'un template ou passez commande auprès de l'équipe Pixlide."
          action={
            <Link to="/creer" className={buttonClasses({ size: 'sm' })}>
              Créer une vidéo
            </Link>
          }
        />
      ) : null}

      {items.length > 0 ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((project) => {
            const badge = statusBadge(project);

            return (
              <Card key={project.id} className="flex flex-col gap-3 p-4">
                <div className="flex items-center justify-between gap-2 border-b border-ink-100 pb-3">
                  <p className="truncate text-sm font-semibold text-ink-900">{project.name}</p>
                  <Badge tone={badge.tone}>{badge.label}</Badge>
                </div>

                {project.status === 0 ? (
                  <DraftActions project={project} />
                ) : (
                  <div
                    className={cn(
                      'flex items-center justify-center overflow-hidden rounded-input bg-ink-900',
                      frameClassForFormat(project.format),
                    )}
                  >
                    {project.publishedVideoUrl ? (
                      <ProtectedVideoPreview
                        projectId={project.id}
                        videoId={`${project.id}:${project.lastRenderedAt ?? ''}`}
                        url={project.publishedVideoUrl}
                        posterUrl={project.publishedPosterUrl}
                        name={project.name}
                      />
                    ) : project.status === 1 ? (
                      <span
                        role="status"
                        className="flex flex-col items-center gap-2 px-3 text-center text-xs text-white/70"
                      >
                        <Loader2 aria-hidden className="size-6 animate-spin" strokeWidth={1.5} />
                        {renderWaitLabel(project.renderStartedAt, project.estimatedRenderSeconds, now)}
                      </span>
                    ) : (
                      <PlayCircle aria-hidden className="size-10 text-white/70" strokeWidth={1} />
                    )}
                  </div>
                )}

                <div className="flex divide-x divide-ink-100 border-t border-ink-100 pt-3">
                  <CardStat
                    icon={<CalendarDays className="size-3.5" />}
                    label="Modifiée le"
                    value={formatShortDate(project.lastTouchedAt)}
                  />
                  <CardStat
                    icon={<Clock className="size-3.5" />}
                    label="Durée"
                    value={formatDuration(project.publishedDurationMs)}
                  />
                  <CardStat
                    icon={<Monitor className="size-3.5" />}
                    label="Écrans en diffusion"
                    value={String(project.broadcastScreenCount)}
                  />
                </div>
              </Card>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
